import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, AlertCircle } from 'lucide-react';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NouvellesReglementationsANJ2025 = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/news" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux actualités
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12" itemScope itemType="https://schema.org/NewsArticle">
          <div className="flex items-center gap-2 text-sm text-purple-600 mb-4">
            <time dateTime="2025-11-30" itemProp="datePublished">30.11.2025</time>
            <span className="text-gray-400">•</span>
            <span className="font-semibold" itemProp="articleSection">Réglementation</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" itemProp="headline">
            Nouvelles Réglementations ANJ : Ce Qui Change en 2025
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              L'Autorité Nationale des Jeux (ANJ) a annoncé une série de mesures réglementaires qui vont transformer le paysage des paris 
              en ligne en France. Ces changements, qui entrent en vigueur progressivement tout au long de 2025, visent à renforcer la 
              protection des joueurs tout en améliorant la transparence du marché. Voici ce que vous devez savoir et comment ces évolutions 
              vont impacter votre expérience de parieur.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-600" />
                Renforcement des Obligations de Transparence sur les Bonus
              </h2>
              <p>
                L'une des mesures les plus importantes concerne l'affichage des bonus et promotions. Désormais, tous les sites agréés doivent 
                afficher de manière claire et lisible les conditions de mise associées à chaque bonus, directement sur la page promotionnelle. 
                Plus de conditions cachées dans les petits caractères : les informations essentielles doivent être visibles avant même 
                d'accepter l'offre.
              </p>
              <p>
                Cette mesure répond à une préoccupation majeure des parieurs : trop souvent, les conditions réelles des bonus n'étaient 
                découvertes qu'après l'inscription, créant frustration et sentiment de tromperie. Maintenant, vous pourrez comparer 
                objectivement les offres avant de vous engager, en sachant exactement ce que chaque bonus implique en termes de conditions 
                de mise et de délais.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Nouveaux Outils de Jeu Responsable Obligatoires
            </h2>
            <p>
              Tous les sites agréés doivent désormais proposer des outils de jeu responsable plus accessibles et plus efficaces. Les limites 
              de dépôt, de mise, et de temps doivent être configurables directement depuis le compte, sans avoir à contacter le service client. 
              De plus, ces limites doivent être activables immédiatement, sans délai d'attente.
            </p>
            <p>
              L'auto-exclusion temporaire devient aussi plus flexible : vous pouvez désormais choisir des périodes personnalisées (pas seulement 
              les durées prédéfinies), et l'activation doit être instantanée. Ces améliorations répondent aux retours des associations de 
              protection des joueurs qui soulignaient la difficulté d'accès à ces outils essentiels.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-blue-600" />
                Obligation de Vérification d'Identité Renforcée
              </h2>
              <p>
                Pour lutter contre le blanchiment d'argent et la fraude, l'ANJ impose désormais une vérification d'identité obligatoire 
                avant le premier retrait, quel que soit le montant. Cette mesure, qui peut sembler contraignante, protège en réalité tous 
                les joueurs légitimes en empêchant les comptes frauduleux d'opérer.
              </p>
              <p>
                La bonne nouvelle : les sites doivent traiter ces vérifications dans un délai maximum de 48 heures, et une fois vérifié, 
                vous n'aurez plus besoin de refournir ces documents pour les retraits futurs. Cette mesure améliore aussi la sécurité 
                globale du marché en décourageant les pratiques frauduleuses.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Encadrement Stricte des Publicités Promotionnelles
            </h2>
            <p>
              Les publicités pour les sites de paris sont désormais soumises à des règles plus strictes. Les messages doivent être clairs 
              sur les risques, éviter les promesses irréalistes, et ne pas cibler les publics vulnérables. Les bonus doivent être présentés 
              avec leurs conditions principales, pas seulement leurs montants attractifs.
            </p>
            <p>
              Cette mesure vise à réduire les attentes irréalistes et les déceptions. Les sites ne pourront plus promettre "des gains faciles" 
              ou présenter les paris comme une source de revenus garantie. Cette transparence accrue vous permet de faire des choix éclairés 
              basés sur la réalité des offres, pas sur des promesses marketing.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-green-600" />
                Délais de Retrait Maximum Garantis
              </h2>
              <p>
                Une nouveauté importante : tous les sites doivent désormais garantir des délais de retrait maximum selon la méthode choisie. 
                Si un retrait dépasse le délai annoncé sans raison valable (comme une vérification d'identité en cours), le site doit 
                expliquer le retard et proposer une solution. Cette mesure protège les joueurs contre les retards injustifiés qui étaient 
                parfois utilisés pour retenir les fonds.
              </p>
              <p>
                Les sites doivent aussi afficher clairement ces délais sur leurs pages de retrait, permettant une comparaison objective 
                entre les différentes méthodes disponibles. Vous saurez exactement combien de temps attendre avant de recevoir vos gains, 
                ce qui facilite la planification et réduit l'anxiété liée aux retraits.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Renforcement de la Protection des Données
            </h2>
            <p>
              Les nouvelles réglementations renforcent aussi la protection de vos données personnelles. Les sites doivent obtenir votre 
              consentement explicite avant de partager vos informations avec des partenaires, et vous devez pouvoir refuser ces partages 
              sans impact sur votre accès aux services. Cette mesure vous donne un contrôle total sur vos données.
            </p>
            <p>
              De plus, les sites doivent maintenant vous permettre de télécharger toutes vos données personnelles en format lisible, et 
              de demander leur suppression si vous fermez votre compte. Ces droits, déjà prévus par le RGPD, sont désormais explicitement 
              encadrés pour le secteur des paris en ligne.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Impact sur Votre Expérience de Parieur
            </h2>
            <p>
              Ces nouvelles réglementations vont globalement améliorer votre expérience en tant que parieur. Plus de transparence signifie 
              moins de surprises désagréables. Des outils de jeu responsable plus accessibles signifient une meilleure protection. Des délais 
              garantis signifient moins d'incertitude sur les retraits.
            </p>
            <p>
              Cependant, certains changements peuvent sembler contraignants à première vue. La vérification d'identité obligatoire peut 
              ralentir votre premier retrait, mais elle protège aussi votre compte contre la fraude. Les règles plus strictes sur les 
              publicités peuvent rendre les offres moins "flashy", mais elles garantissent que ce qui est promis correspond à la réalité.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Ce Que Vous Devez Faire
            </h2>
            <p>
              Pour tirer le meilleur parti de ces nouvelles réglementations, familiarisez-vous avec les nouveaux outils disponibles sur 
              votre site de paris. Configurez vos limites de jeu responsable dès maintenant, même si vous n'en avez pas besoin immédiatement. 
              Préparez vos documents d'identité pour accélérer votre premier retrait.
            </p>
            <p>
              Lisez attentivement les nouvelles conditions générales que les sites doivent mettre à jour. Comparez les offres en vous basant 
              sur les informations maintenant obligatoirement affichées. Et surtout, utilisez ces nouvelles protections : elles sont là pour 
              vous aider à parier de manière plus sûre et plus responsable.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Conclusion
              </h2>
              <p>
                Ces nouvelles réglementations représentent une évolution positive du marché français des paris en ligne. Elles renforcent 
                la protection des joueurs tout en améliorant la transparence et la confiance. En tant que parieur, vous bénéficiez de 
                plus d'informations, de plus de protection, et de plus de contrôle sur votre expérience. Ces changements peuvent nécessiter 
                quelques ajustements de votre part, mais ils créent un environnement plus sûr et plus équitable pour tous.
              </p>
            </div>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default NouvellesReglementationsANJ2025;

